import { Link } from 'react-router-dom';
import LegalLayout from '../components/legal/LegalLayout.jsx';

const TOC = [
  { id: 'que-son', label: '1. Qué son las cookies' },
  { id: 'tipos', label: '2. Tipos de cookies que usamos' },
  { id: 'detalle', label: '3. Listado detallado' },
  { id: 'terceros', label: '4. Cookies de terceros' },
  { id: 'duracion', label: '5. Duración' },
  { id: 'gestion', label: '6. Cómo gestionarlas' },
  { id: 'navegador', label: '7. Configurar tu navegador' },
  { id: 'consecuencias', label: '8. Si las desactivas' },
  { id: 'cambios', label: '9. Cambios en esta política' },
  { id: 'contacto', label: '10. Contacto' },
];

export default function CookiesPage() {
  function resetCookies() {
    try {
      localStorage.removeItem('rc-cookies');
    } catch {
      // ignore
    }
    window.location.reload();
  }

  return (
    <LegalLayout
      title="Política de Cookies"
      intro="Aquí explicamos qué cookies usamos en esta web, para qué sirven, cuánto tiempo se quedan y cómo puedes gestionarlas o desactivarlas en cualquier momento. Sin tecnicismos innecesarios."
      meta={[
        { label: 'Última actualización', value: '5 de mayo de 2026' },
        { label: 'Versión', value: '1.0' },
        { label: 'Vigencia', value: 'Inmediata' },
      ]}
      toc={TOC}
    >
      <div className="legal-callout">
        <strong>Resumen rápido:</strong> Usamos cookies técnicas (necesarias para que la web
        funcione, siempre activas) y, si las aceptas, cookies analíticas para entender cómo se usa
        el sitio. No usamos cookies para perfiles publicitarios. Puedes cambiar tu decisión en
        cualquier momento desde el botón de abajo.
      </div>

      <h2 id="que-son">1. Qué son las cookies</h2>
      <p>
        Una cookie es un pequeño fichero de texto que un sitio web guarda en tu navegador cuando lo
        visitas. Sirve para recordar información sobre tu visita: idioma elegido, preferencias de
        tema, si ya has aceptado el aviso de cookies, etc. También puede usarse para entender cómo
        navegas por la web (de forma anónima y agregada) o para mostrarte contenido más relevante.
      </p>
      <p>
        Existen tecnologías similares (almacenamiento local del navegador, píxeles, etiquetas) que
        cumplen una función parecida. En esta política, cuando hablamos de “cookies”, nos referimos
        a todas ellas de forma conjunta.
      </p>

      <h2 id="tipos">2. Tipos de cookies que usamos</h2>
      <p>Clasificamos las cookies según tres criterios:</p>

      <h3>Según quién las gestiona</h3>
      <ul>
        <li>
          <strong>Propias</strong>: las gestionamos directamente nosotros desde el dominio de Random
          College.
        </li>
        <li>
          <strong>De terceros</strong>: las gestionan otros proveedores que nos prestan servicios
          (analítica, vídeo, etc.). Sus dominios son distintos del nuestro.
        </li>
      </ul>

      <h3>Según su duración</h3>
      <ul>
        <li>
          <strong>De sesión</strong>: se borran automáticamente al cerrar el navegador.
        </li>
        <li>
          <strong>Persistentes</strong>: permanecen en tu dispositivo durante un periodo definido o
          hasta que las elimines manualmente.
        </li>
      </ul>

      <h3>Según su finalidad</h3>
      <ul>
        <li>
          <strong>Técnicas o necesarias</strong>: imprescindibles para que la web funcione
          correctamente. No requieren consentimiento. Ejemplo: recordar tu preferencia de tema
          oscuro o claro.
        </li>
        <li>
          <strong>De preferencias</strong>: recuerdan opciones que has elegido tú (idioma,
          configuraciones). Mejoran la experiencia pero no son imprescindibles.
        </li>
        <li>
          <strong>Analíticas</strong>: nos ayudan a entender cómo se usa la web, qué páginas se
          visitan más, dónde aterrizan los usuarios, etc. Los datos se tratan de forma agregada y
          anónima. Solo se activan si las aceptas.
        </li>
        <li>
          <strong>De marketing</strong>: para mostrarte publicidad relevante en otras plataformas.{' '}
          <strong>Actualmente no usamos cookies de marketing en esta web.</strong> Si en el futuro
          las añadimos, te lo notificaremos y te pediremos consentimiento de nuevo.
        </li>
      </ul>

      <h2 id="detalle">3. Listado detallado de cookies</h2>
      <p>
        A continuación detallamos las cookies y tecnologías de almacenamiento que usamos
        actualmente:
      </p>

      <table className="legal-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Tipo</th>
            <th>Finalidad</th>
            <th>Duración</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>rc-theme</code>
            </td>
            <td>Técnica (propia)</td>
            <td>Recuerda tu preferencia de tema (claro u oscuro) entre visitas.</td>
            <td>Persistente</td>
          </tr>
          <tr>
            <td>
              <code>rc-cookies</code>
            </td>
            <td>Técnica (propia)</td>
            <td>Almacena tu decisión sobre el aviso de cookies para no volver a mostrártelo.</td>
            <td>Persistente</td>
          </tr>
          <tr>
            <td>
              <code>_ga</code>, <code>_ga_*</code>
            </td>
            <td>Analítica (terceros: Google)</td>
            <td>
              Distingue usuarios de forma anónima para medir el uso de la web mediante Google
              Analytics.
            </td>
            <td>2 años</td>
          </tr>
          <tr>
            <td>
              <code>_gid</code>
            </td>
            <td>Analítica (terceros: Google)</td>
            <td>Distingue usuarios de forma anónima durante 24 horas.</td>
            <td>24 horas</td>
          </tr>
        </tbody>
      </table>

      <p>
        <em>Nota:</em> Las cookies técnicas (<code>rc-theme</code> y <code>rc-cookies</code>)
        técnicamente son almacenamiento local del navegador (<code>localStorage</code>), pero las
        incluimos aquí por transparencia ya que cumplen una función equivalente a una cookie.
      </p>

      <p>
        El listado puede variar si añadimos o retiramos servicios. Mantenemos esta tabla
        actualizada y te avisaremos si introducimos cookies nuevas que requieran tu consentimiento.
      </p>

      <h2 id="terceros">4. Cookies de terceros</h2>
      <p>
        Algunos servicios externos que usamos pueden instalar cookies en tu navegador cuando
        interactúas con sus elementos en nuestra web:
      </p>
      <ul>
        <li>
          <strong>Google Analytics</strong>: para medir el tráfico y entender cómo se usa la web.
          Anonimizamos las direcciones IP.{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Política de privacidad de Google
          </a>
          .
        </li>
        <li>
          <strong>Skool</strong>: si haces click en “Acceder a Skool”, serás redirigido a la
          plataforma de Skool, que usa sus propias cookies.{' '}
          <a href="https://www.skool.com/privacy" target="_blank" rel="noopener noreferrer">
            Política de Skool
          </a>
          .
        </li>
        <li>
          <strong>Google Fonts</strong>: la tipografía Inter se carga desde los servidores de
          Google. Esto puede implicar el envío de tu IP a Google.{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
            Política de privacidad de Google
          </a>
          .
        </li>
      </ul>
      <p>
        No tenemos control directo sobre las cookies de terceros. Te recomendamos consultar las
        políticas de privacidad enlazadas para más información.
      </p>

      <h2 id="duracion">5. Cuánto duran las cookies</h2>
      <p>La duración de cada cookie está indicada en la tabla del apartado 3. En general:</p>
      <ul>
        <li>
          Las cookies <strong>técnicas y de preferencias</strong> se mantienen mientras las
          necesites o hasta que las borres manualmente.
        </li>
        <li>
          Las cookies <strong>analíticas</strong> de Google Analytics tienen una duración máxima de
          2 años.
        </li>
        <li>
          Las cookies <strong>de sesión</strong> se eliminan al cerrar el navegador.
        </li>
      </ul>
      <p>Puedes borrar todas las cookies en cualquier momento desde la configuración de tu navegador.</p>

      <h2 id="gestion">6. Cómo gestionar tus cookies en Random College</h2>
      <p>
        La primera vez que visitas la web, te mostramos un banner para que decidas si aceptas o
        rechazas las cookies no esenciales. Tu decisión se guarda y no volveremos a preguntártela.
      </p>
      <p>Si quieres cambiar tu decisión:</p>
      <ol>
        <li>
          Borra los datos de almacenamiento de esta web desde la configuración de tu navegador
          (busca “datos del sitio” o “almacenamiento local”).
        </li>
        <li>Recarga la página: el banner volverá a aparecer y podrás elegir de nuevo.</li>
      </ol>

      <p>O usa el botón directo:</p>
      <p>
        <button type="button" className="legal-action" onClick={resetCookies}>
          Restablecer preferencias de cookies
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
          </svg>
        </button>
      </p>

      <h2 id="navegador">7. Configurar tu navegador</h2>
      <p>
        También puedes bloquear o eliminar cookies directamente desde tu navegador. Aquí tienes los
        enlaces a las guías oficiales:
      </p>
      <ul>
        <li>
          <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">
            Google Chrome
          </a>
        </li>
        <li>
          <a
            href="https://support.mozilla.org/es/kb/proteccion-antirastreo-mejorada-firefox-escritorio"
            target="_blank"
            rel="noopener noreferrer"
          >
            Mozilla Firefox
          </a>
        </li>
        <li>
          <a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">
            Safari (macOS)
          </a>
        </li>
        <li>
          <a href="https://support.apple.com/es-es/HT201265" target="_blank" rel="noopener noreferrer">
            Safari (iOS)
          </a>
        </li>
        <li>
          <a
            href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
            target="_blank"
            rel="noopener noreferrer"
          >
            Microsoft Edge
          </a>
        </li>
      </ul>

      <h2 id="consecuencias">8. Qué pasa si desactivas las cookies</h2>
      <p>
        Si rechazas todas las cookies, la web seguirá funcionando con normalidad — solo se
        desactivarán las funciones que dependan de cookies opcionales:
      </p>
      <ul>
        <li>
          No podremos medir cómo se usa la web (perderemos información para mejorarla, pero tu
          experiencia no se verá afectada).
        </li>
        <li>
          Algunas preferencias podrían no recordarse entre sesiones (por ejemplo, tu elección de
          tema claro u oscuro).
        </li>
      </ul>
      <p>
        Las cookies estrictamente necesarias (las que no requieren consentimiento) se mantienen
        activas siempre, ya que sin ellas la web no funcionaría correctamente.
      </p>

      <h2 id="cambios">9. Cambios en esta política</h2>
      <p>
        Podemos actualizar esta política para reflejar cambios en las cookies que usamos, en la
        legislación aplicable o en nuestros servicios. La fecha de última actualización siempre se
        muestra al principio del documento. Cuando hagamos cambios significativos, te lo
        notificaremos mediante el banner de cookies para que puedas revisar tu decisión.
      </p>

      <h2 id="contacto">10. Contacto</h2>
      <p>Si tienes cualquier duda sobre esta Política de Cookies, escríbenos:</p>
      <ul>
        <li>
          <strong>Email</strong>: <a href="mailto:hola@randomcollege.com">hola@randomcollege.com</a>
        </li>
        <li>
          <strong>Asunto sugerido</strong>: “Cookies — [tu consulta]”
        </li>
        <li>
          <strong>Tiempo medio de respuesta</strong>: menos de 48 horas laborables
        </li>
      </ul>

      <p>
        Para más detalles sobre el tratamiento de tus datos personales, consulta también nuestra{' '}
        <Link to="/privacidad">Política de Privacidad</Link> y nuestros{' '}
        <Link to="/terminos">Términos y Condiciones</Link>.
      </p>

      <div className="legal-callout" style={{ marginTop: 48 }}>
        <strong>Aviso:</strong> Esta política es una plantilla base que necesita ser revisada y
        completada con los datos legales reales de Random College (razón social, NIF, domicilio) y
        con el listado definitivo de proveedores y cookies activas antes de publicarse. Recomendamos
        que un abogado especializado en protección de datos revise el documento final antes de su
        entrada en vigor.
      </div>
    </LegalLayout>
  );
}
